import React from 'react'
import Image from 'next/image'
import Head from 'next/head'
import Link from 'next/link'
import styled from '@emotion/styled'
import { gsColors as c } from '../../styles/Variables'
import { LinkButton } from '../../styles/Components'
import { mq } from '../../helpers'
import { authors, getAuthor } from '../../data/authors'

export async function getStaticPaths() {
  const paths = Object.keys(authors).map((slug) => ({ params: { slug } }))
  return { paths, fallback: false }
}

export async function getStaticProps({ params }) {
  const author = getAuthor(params.slug)
  if (!author) {
    return { notFound: true }
  }
  return { props: { author } }
}

export default function AuthorPage({ author }) {
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: author.name,
    url: `https://glamsquad.com/author/${author.slug}`,
    image: `https://glamsquad.com${author.headshot}`,
    jobTitle: author.title,
    worksFor: {
      '@type': 'Organization',
      name: 'Glamsquad',
      url: 'https://glamsquad.com',
    },
    sameAs: [author.instagram, author.linkedin, author.website].filter(Boolean),
    description: author.shortBio,
    knowsAbout: author.expertise,
    alumniOf: {
      '@type': 'EducationalOrganization',
      name: author.education,
    },
  }

  const schemaScript = JSON.stringify(personSchema)

  return (
    <>
      <Head>
        <title>{author.name} — GLAMSQUAD</title>
        <meta name="title" content={`${author.name} — GLAMSQUAD`} />
        <meta name="description" content={author.metaDescription || author.shortBio} />
        <link
          rel="canonical"
          href={`https://glamsquad.com/author/${author.slug}`}
        />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={`${author.name} — GLAMSQUAD`} />
        <meta property="og:description" content={author.metaDescription || author.shortBio} />
        <meta property="og:url" content={`https://glamsquad.com/author/${author.slug}`} />
        <meta property="og:image" content={`https://glamsquad.com${author.headshot}`} />
        <script type="application/ld+json">{schemaScript}</script>
      </Head>

      <PageWrapper>
        {/* Hero */}
        <HeroSection>
          <div className="headshot">
            <Image
              src={author.headshot}
              alt={author.name}
              fill
            />
          </div>
          <div className="info">
            {author.linkedin ? (
              <a
                className="name-link"
                href={author.linkedin}
                target="_blank"
                rel="noopener noreferrer"
              >
                <h1>{author.name}</h1>
                <p className="author-title">{author.title}</p>
              </a>
            ) : (
              <>
                <h1>{author.name}</h1>
                <p className="author-title">{author.title}</p>
              </>
            )}
            <div className="stats">
              <div className="stat">
                <span className="stat-label">Expertise</span>
                <span className="stat-value">{author.expertise}</span>
              </div>
              <div className="divider" />
              <div className="stat">
                <span className="stat-label">Education</span>
                <span className="stat-value">
                  {author.educationUrl ? (
                    <a
                      href={author.educationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {author.education}
                    </a>
                  ) : (
                    author.education
                  )}
                </span>
              </div>
            </div>
          </div>
        </HeroSection>

        <ContentWrapper>
          {/* Highlights */}
          <Section>
            <SectionTitle>Highlights</SectionTitle>
            <HighlightsList>
              {author.highlights.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </HighlightsList>
          </Section>

          {/* Experience */}
          <Section>
            <SectionTitle>Experience</SectionTitle>
            <ExperienceGrid>
              <div className="narrative">
                {author.experience.split('\n\n').map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
              <QuoteBlock>
                <div className="quote-icon">&ldquo;</div>
                <blockquote>{author.quote.text}</blockquote>
                <cite>&mdash; {author.quote.attribution}</cite>
              </QuoteBlock>
            </ExperienceGrid>
          </Section>

          {/* Education */}
          <Section>
            <SectionTitle>Education</SectionTitle>
            <p className="education-text">
              {author.educationUrl ? (
                <a
                  href={author.educationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {author.education}
                </a>
              ) : (
                author.education
              )}
              {author.degree ? ` — ${author.degree}` : ''}
            </p>
          </Section>

          {/* CTA */}
          <CtaSection>
            <Link href="/book" legacyBehavior>
              <LinkButton large>Book Now</LinkButton>
            </Link>
          </CtaSection>
        </ContentWrapper>
      </PageWrapper>
    </>
  )
}

const PageWrapper = styled.div`
  width: 100%;
  max-width: 1660px;
  margin: 0 auto;
`

const HeroSection = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  padding: 60px 88px;
  background-color: ${c.khaki};

  ${mq('max', '800px')} {
    flex-direction: column;
    text-align: center;
    padding: 40px 24px;
    gap: 24px;
  }

  .headshot {
    width: 150px;
    height: 150px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    position: relative;

    img {
      object-fit: cover;
      object-position: center 0;
    }
  }

  .info {
    min-width: 0;

    .name-link {
      display: block;
      text-decoration: none;
      color: inherit;

      &:hover h1 {
        text-decoration: underline;
      }
    }

    h1 {
      font-family: Garnett;
      font-size: 42px;
      font-weight: 750;
      line-height: 140%;
      color: ${c.black};
      margin: 0;

      ${mq('max', '800px')} {
        font-size: 28px;
      }
    }

    .author-title {
      font-family: Euclid;
      font-size: 16px;
      color: ${c.darkGrey};
      margin: 8px 0 24px;

      ${mq('max', '800px')} {
        margin: 4px 0 20px;
        font-size: 14px;
      }
    }
  }

  .stats {
    display: flex;
    align-items: flex-start;
    gap: 32px;

    ${mq('max', '800px')} {
      justify-content: center;
      gap: 24px;
    }
  }

  .divider {
    width: 1px;
    height: 40px;
    background-color: ${c.lightTangerine};
  }

  .stat {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .stat-label {
    font-family: Euclid;
    font-size: 14px;
    font-weight: 600;
    color: ${c.black};
  }

  .stat-value {
    font-family: Euclid;
    font-size: 14px;
    color: ${c.darkGrey};

    a {
      color: ${c.sunsetOrange};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 60px 24px 80px;

  .education-text {
    font-family: Euclid;
    font-size: 20px;
    color: ${c.black};
    line-height: 160%;
    margin: 0;

    ${mq('max', '800px')} {
      font-size: 16px;
    }

    a {
      color: ${c.sunsetOrange};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`

const Section = styled.section`
  margin-bottom: 56px;
`

const SectionTitle = styled.h2`
  font-family: Garnett;
  font-size: 28px;
  font-weight: 750;
  color: ${c.black};
  margin: 0 0 24px;
  line-height: 140%;

  ${mq('max', '800px')} {
    font-size: 22px;
    margin-bottom: 16px;
  }
`

const HighlightsList = styled.ul`
  list-style: disc;
  padding-left: 24px;
  margin: 0;

  li {
    font-family: Euclid;
    font-size: 20px;
    color: ${c.black};
    line-height: 180%;

    ${mq('max', '800px')} {
      font-size: 16px;
    }
  }
`

const ExperienceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: start;

  ${mq('max', '800px')} {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .narrative p {
    font-family: Euclid;
    font-size: 18px;
    color: ${c.black};
    line-height: 170%;
    margin: 0 0 20px;

    ${mq('max', '800px')} {
      font-size: 16px;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const QuoteBlock = styled.div`
  padding: 32px;
  border-left: 4px solid ${c.sunsetOrange};

  ${mq('max', '800px')} {
    padding: 24px;
  }

  .quote-icon {
    font-family: Garnett;
    font-size: 60px;
    color: ${c.sunsetOrange};
    line-height: 1;
    margin-bottom: 8px;
  }

  blockquote {
    font-family: Garnett;
    font-size: 24px;
    font-weight: 600;
    font-style: italic;
    color: ${c.black};
    line-height: 150%;
    margin: 0 0 16px;

    ${mq('max', '800px')} {
      font-size: 20px;
    }
  }

  cite {
    font-family: Euclid;
    font-size: 14px;
    font-style: normal;
    color: ${c.darkGrey};
  }
`

const CtaSection = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 16px;
`
